import { ResponsiveChoropleth } from "@nivo/geo";
import { geoFeatures } from "../data/mockGeoFeatures";
import { tokens } from "../theme";



// ---------------- helpers ----------------
function quantileThresholds(values, k = 5) {
  const v = (values ?? []).filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (v.length === 0) return [];

  const t = [];
  for (let i = 1; i < k; i++) {
    const idx = Math.floor((i * v.length) / k);
    t.push(v[Math.min(idx, v.length - 1)]);
  }
  return Array.from(new Set(t)).sort((a, b) => a - b);
}


function applyTopOutlierSplit(thresholds, values, { ratio = 2.2 } = {}) {
  const v = (values ?? []).filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (v.length < 3 || thresholds.length < 2) return thresholds;

  const max = v[v.length - 1];
  const second = v[v.length - 2];
  if (!Number.isFinite(max) || !Number.isFinite(second) || second <= 0) return thresholds;

  if (max / second >= ratio) {
    const t = thresholds.slice();
    t[t.length - 1] = second;
    return Array.from(new Set(t)).sort((a, b) => a - b);
  }
  return thresholds;
}

function classIndex(v, thresholds) {
  let i = 0;
  while (i < thresholds.length && v > thresholds[i]) i++;
  return i; 
}

function buildLegend(thresholds) {
  const th = Array.isArray(thresholds) ? thresholds.slice().sort((a, b) => a - b) : [];
  if (th.length === 0) return [{ label: "0", i: 0 }];

  const items = [];
  const first = Math.round(th[0]);
  items.push({ label: `≤ ${first}`, i: 0 });

  for (let i = 1; i < th.length; i++) {
    const a = Math.round(th[i - 1]);
    const b = Math.round(th[i]);
    if (b <= a) continue;
    items.push({ label: `${a + 1}–${b}`, i });
  }

  const last = Math.round(th[th.length - 1]);
  items.push({ label: `> ${last}`, i: th.length });

  return items;
}

// ---------------- component ----------------
const GeographyChart = ({ isDashboard = false, data }) => {
  const color = tokens();
  const PALETTE_5 = [color.orange[100], color.orange[200], color.orange[300], color.orange[400], color.orange[500]];
  
  const mappedFeatures = (geoFeatures?.features ?? []).map((f) => ({
    ...f,
    id: String(Number(f?.properties?.codregion)),
  }));


  const inputData = Array.isArray(data) ? data : [];
  const raw = inputData
    .map((d) => {
      const value = Number(d?.value);

      return {
        id: String(Number(d?.id)),
        valueReal: Number.isFinite(value) ? value : null,
      };
    })
    .filter((d) => d.id && d.valueReal != null);

  const values = raw.map((d) => d.valueReal).filter((v) => v >= 0);


  const K = 5;
  let thresholds = quantileThresholds(values, K);
  thresholds = applyTopOutlierSplit(thresholds, values, { ratio: 2.2 });

  const legend = buildLegend(thresholds);


  const chartData = raw.map((d) => ({
    id: d.id,
    value: classIndex(d.valueReal, thresholds), 
    valueReal: d.valueReal,
  }));

  return (
    <div style={{ width: "100%", height: "100%", minHeight: 10, position: "relative" }}>
      <div style={{ position: "absolute", inset: 0 }}>
        <ResponsiveChoropleth
          data={chartData}
          features={mappedFeatures}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}

          
          domain={[0, 4]}
          colors={PALETTE_5}

          unknownColor = {color.grey[600]}
          label="properties.Region"

        
          tooltip={({ feature }) => {
            const props = feature?.properties ?? {};
            const regionName = props?.Region ?? props?.region ?? props?.name ?? "Región";
            const vReal = feature?.data?.valueReal;

            return (
              <div
                style={{
                  background: "white",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: `1px solid ${color.grey[700]}`,
                  boxShadow: `0 4px 16px ${color.grey[700]}`,
                  color: color.primary[100],
                  fontSize: 12.5,
                }}
              >
                <div style={{ fontWeight: 900, marginBottom: 4 }}>{regionName}</div>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                  <span style={{ opacity: 0.75 }}>Participantes:</span>
                  <span style={{ fontWeight: 900 }}>
                    {Number.isFinite(vReal) ? Math.round(vReal) : "—"}
                  </span>
                </div>
              </div>
            );
          }}

          projectionType="mercator"
          projectionScale={isDashboard ? 1220 : 1750}
          projectionTranslation={isDashboard ? [5.6, -0.355] : [6, -0.345]}
          projectionRotation={isDashboard ? [0, 0] : [0, 0]}
          borderWidth={1}
          borderColor={color.primary[200]}
        />
      </div>

      {/* Leyenda */}
      <div
        style={{
          position: "absolute",
          left: 15,
          top: 15,
          zIndex: 5,
          background: color.grey[400],
          border: `1px solid ${color.grey[700]}`,
          borderRadius: 12,
          padding: "10px 12px",
          color: color.primary[100],
          fontSize: 12.5,
          lineHeight: 1.1,
          display: "grid",
          rowGap: 6,
          minWidth: 110,
        }}
      >
        {legend.map((it) => (
          <div
            key={it.label}
            style={{
              display: "grid",
              gridTemplateColumns: "12px 1fr",
              columnGap: 8,
              alignItems: "center",
            }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 5,
                background: PALETTE_5[it.i] ?? PALETTE_5[PALETTE_5.length - 1],
                display: "block",
                flexShrink: 0,
              }}
            />
            <span style={{ display: "block" }}>{it.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GeographyChart;
